import moment from 'moment';
import { serverApiConfig } from '../config';
import EventEmitter from '../utilities/service';
const { AccountClient } = require('../grpc/generated/account_grpc_web_pb');

export default class UserHistory {
  // Period constants
  static CUSTOM_PERIOD_KEY = 5;
  static DEFAULT_PERIOD_KEY = 1;

  // Mode constants
  static MODE_DEALS = 1;
  static MODE_POSITIONS = 2;
  static MODE_ORDERS = 3;
  static MODE_ALL = 4;

  constructor() {
    // Track loading state for each mode
    this.loadingStates = {
      [UserHistory.MODE_DEALS]: false,
      [UserHistory.MODE_POSITIONS]: false,
      [UserHistory.MODE_ORDERS]: false,
      [UserHistory.MODE_ALL]: false
    };
  }

  /**
   * Set loading state for a specific mode
   * @param {number} mode - Mode type
   * @param {boolean} isLoading - Loading state
   */
  setLoading(mode, isLoading) {
    this.loadingStates[mode] = isLoading;
    this.emitLoadingEvent(mode, isLoading);
  }

  /**
   * Get loading state for a specific mode
   * @param {number} mode - Mode type
   * @returns {boolean}
   */
  isLoading(mode) {
    return this.loadingStates[mode] || false;
  }

  /**
   * Emit loading event through EventEmitter
   * @param {number} mode - Mode type
   * @param {boolean} isLoading - Loading state
   */
  emitLoadingEvent(mode, isLoading) {
    EventEmitter.dispatch('history:loading', {
      mode,
      isLoading
    });
  }

  /**
   * Check if period key is custom period
   * @param {number} periodKey - Period key
   * @returns {boolean}
   */
  isCustomPeriod(periodKey) {
    return periodKey === UserHistory.CUSTOM_PERIOD_KEY;
  }

  /**
   * Validate custom period parameters
   * @param {number} fromCustom - Start timestamp
   * @param {number} toCustom - End timestamp
   * @returns {boolean}
   */
  validateCustomPeriod(fromCustom, toCustom) {
    return !!(fromCustom && toCustom);
  }

  /**
   * Resolve period range from either custom values or period key
   * @param {number} periodKey - Period key
   * @param {number} fromCustom - Custom start timestamp
   * @param {number} toCustom - Custom end timestamp
   * @returns {Object|null} { from, to, label } or null if invalid
   */
  resolvePeriodRange(periodKey, fromCustom, toCustom) {
    // Custom period
    if (this.isCustomPeriod(periodKey)) {
      if (!this.validateCustomPeriod(fromCustom, toCustom)) {
        console.warn(
          '[UserHistory] Custom period requires valid fromCustom and toCustom parameters'
        );
        return null;
      }
      return {
        from: fromCustom,
        to: toCustom,
        label: 'Custom Period'
      };
    }

    // Use custom range if provided, regardless of period key
    if (this.validateCustomPeriod(fromCustom, toCustom)) {
      return {
        from: fromCustom,
        to: toCustom,
        label: 'Custom Period'
      };
    }

    // Use predefined period
    return this.getPeriodRange(periodKey);
  }

  /**
   * Convert period key to date range
   * @param {number} periodKey - Period key (7=today, 1=1month, 2=3months, 3=6months, 4=1year)
   * @returns {Object} { from, to } timestamps in seconds
   * @note All dates use UTC+0 timezone
   */
  getPeriodRange(periodKey) {
    const today = moment().utc().startOf('day');
    const tomorrow = today.clone().add(1, 'day');

    const periodMap = {
      7: {
        from: today.clone().add(1, 'second').unix(),
        to: tomorrow.unix(),
        label: 'Today'
      },
      0: {
        from: (() => {
          const weekday = tomorrow.isoWeekday(); // 1=Mon ... 7=Sun
          const daysToSubtract = weekday === 7 ? 7 : weekday;
          return tomorrow.clone().subtract(daysToSubtract, 'days').startOf('day').unix();
        })(),
        to: tomorrow.unix(),
        label: 'Last Week'
      },
      1: {
        from: tomorrow.clone().subtract(30, 'days').unix(),
        to: tomorrow.unix(),
        label: 'Last Month'
      },
      2: {
        from: tomorrow.clone().subtract(90, 'days').unix(),
        to: tomorrow.unix(),
        label: 'Last 3 Months'
      },
      3: {
        from: today.clone().subtract(180, 'days').unix(),
        to: tomorrow.unix(),
        label: 'Last 6 Months'
      },
      4: {
        from: tomorrow.clone().subtract(1, 'year').unix(),
        to: tomorrow.unix(),
        label: '1 Year'
      },
      6: {
        from: 1,
        to: tomorrow.unix(),
        label: 'All'
      }
    };

    return periodMap[periodKey] || periodMap[UserHistory.DEFAULT_PERIOD_KEY];
  }

  /**
   * Fetch data for the specified period
   * @param {Object} request - gRPC request object
   * @param {Object} metadata - gRPC metadata
   * @param {number} mode - Mode (1=deals, 2=positions, 3=orders)
   * @param {number} periodKey - Period key (7=today, 1=1month, etc., 5=custom)
   * @param {string} accountId - Account ID
   * @param {number} fromCustom - Custom start timestamp (optional)
   * @param {number} toCustom - Custom end timestamp (optional)
   * @returns {Promise<Array>} Filtered data
   */
  async smartFilter(request, metadata, mode, periodKey, accountId, fromCustom, toCustom) {
    try {
      // Set loading state to true
      this.setLoading(mode, true);

      // Resolve period range dynamically
      const periodRange = this.resolvePeriodRange(periodKey, fromCustom, toCustom);

      // Return early if period range is invalid
      if (!periodRange) {
        this.setLoading(mode, false);
        return [];
      }

      const { from, to } = periodRange;
      // Fetch data from API
      const fetchedData = await this.fetchRange(request, metadata, mode, from, to, accountId);

      // Emit data
      this.emitHistoryEvent(mode, fetchedData);

      // Set loading state to false
      this.setLoading(mode, false);

      return fetchedData;
    } catch (error) {
      console.error('[UserHistory] Fetch error:', error);
      // Set loading state to false on error
      this.setLoading(mode, false);
      throw error;
    }
  }

  /**
   * Emit history event through EventEmitter
   * @param {number} mode - Mode type
   * @param {Array} data - History data
   */
  emitHistoryEvent(mode, data) {
    EventEmitter.dispatch('history', {
      mode,
      data: this.createResponse(data),
      cached: false
    });
  }

  /**
   * Extract data list from response based on mode
   * @param {Object} responseData - Parsed response data
   * @param {number} mode - Mode type
   * @returns {Array} Data list
   */
  extractDataByMode(responseData, mode) {
    const modeToListMap = {
      [UserHistory.MODE_DEALS]: 'dealsList',
      [UserHistory.MODE_POSITIONS]: 'positionsList',
      [UserHistory.MODE_ORDERS]: 'ordersList',
      [UserHistory.MODE_ALL]: 'allList'
    };

    const listKey = modeToListMap[mode];
    return listKey ? responseData[listKey] : [];
  }

  /**
   * Clone and configure request object
   * @param {Object} request - Original request
   * @param {number} from - Start timestamp
   * @param {number} to - End timestamp
   * @param {number} mode - Mode type
   * @returns {Object} Configured request
   */
  configureRequest(request, from, to, mode) {
    const clonedRequest = Object.create(Object.getPrototypeOf(request));
    Object.assign(clonedRequest, request);

    clonedRequest.setFrom(from);
    clonedRequest.setTo(to);
    clonedRequest.setMode(mode);
    clonedRequest.setSymbol('');

    return clonedRequest;
  }

  /**
   * Fetch data for a specific range
   * @param {Object} request - gRPC request
   * @param {Object} metadata - gRPC metadata
   * @param {number} mode - Mode (1=deals, 2=positions, 3=orders)
   * @param {number} from - Start timestamp in SECONDS
   * @param {number} to - End timestamp in SECONDS
   * @param {string} accountId - Account ID
   */
  async fetchRange(request, metadata, mode, from, to, accountId) {
    return new Promise((resolve, reject) => {
      const accountClient = new AccountClient(
        serverApiConfig(localStorage.getItem('trade.server')),
        null,
        null
      );

      const configuredRequest = this.configureRequest(request, from, to, mode);

      accountClient.getUserHistory(configuredRequest, metadata, (err, response) => {
        if (err) {
          console.error('[UserHistory] Fetch error:', err);
          reject(err);
          return;
        }

        if (response) {
          const responseData = this.parseResponse(response);

          resolve(responseData);
        } else {
          resolve([]);
        }
      });
    });
  }

  /**
   * Parse gRPC response to extract data array
   */
  parseResponse(response) {
    if (!response) return [];

    // Try different possible response structures
    if (typeof response.toObject === 'function') {
      const obj = response.toObject();
      return obj || [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  /**
   * Create response object from data array
   */
  createResponse(data) {
    // Return the data in a format compatible with the existing system
    return {
      toObject: () => data
    };
  }
}
